import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '@/lib/api'
import type { Contact } from '@/lib/api-types'
import { useSessionStore } from '@/stores/session'

/**
 * Local-dev account switcher — the Stripe-toolbar equivalent. Renders nothing
 * unless the API answers /v1/dev/ping (which only exists when the server runs
 * with the local SF_DEV_ACCESS_TOKEN bypass), so production builds carry the
 * code but never show it. Picking an account swaps the session JWT in place
 * and clears the query cache; the whole app re-renders as that customer.
 */

interface DevAccount {
  id: string
  name: string
  orderCount: number
}

interface SwitchResponse {
  sessionJwt: string
  contact: Contact
  writesLive: boolean
}

export function DevSwitcher() {
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [accounts, setAccounts] = useState<DevAccount[]>([])
  const [busy, setBusy] = useState(false)
  const [writesLive, setWritesLive] = useState<boolean | null>(null)
  const contact = useSessionStore((s) => s.contact)
  const login = useSessionStore((s) => s.login)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE_URL}/v1/dev/ping`)
      .then((r) => {
        if (!cancelled && r.ok) setEnabled(true)
      })
      .catch(() => {
        // no dev API — stay hidden
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!open) return
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => {
      fetch(`${API_BASE_URL}/v1/dev/accounts?q=${encodeURIComponent(query)}`)
        .then((r) => (r.ok ? r.json() : { accounts: [] }))
        .then((d: { accounts: DevAccount[] }) => setAccounts(d.accounts))
        .catch(() => setAccounts([]))
    }, 200)
    return () => clearTimeout(debounce.current)
  }, [open, query])

  if (!enabled) return null

  const switchTo = async (accountId: string) => {
    setBusy(true)
    try {
      const res = await fetch(`${API_BASE_URL}/v1/dev/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId }),
      })
      if (!res.ok) return
      const data = (await res.json()) as SwitchResponse
      login(data.sessionJwt, data.contact)
      setWritesLive(data.writesLive)
      queryClient.clear()
      setOpen(false)
      setQuery('')
      navigate('/')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2 font-mono text-xs">
      {open && (
        <div className="w-80 rounded-lg border border-rule bg-paper p-3 shadow-lg">
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer accounts"
            className="mb-2 w-full rounded border border-rule bg-snow px-2 py-1.5 text-ink outline-none focus:border-brand"
          />
          <ul className="max-h-64 overflow-y-auto">
            {accounts.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void switchTo(a.id)}
                  className="flex w-full items-baseline justify-between gap-2 rounded px-2 py-1.5 text-left text-ink hover:bg-brandTint disabled:opacity-50"
                >
                  <span className="truncate">{a.name}</span>
                  <span className="shrink-0 text-mute">{a.orderCount.toLocaleString()} orders</span>
                </button>
              </li>
            ))}
            {accounts.length === 0 && <li className="px-2 py-1.5 text-mute">No matches</li>}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-amber bg-paper px-3 py-1.5 text-ink shadow-md hover:bg-snow"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-amber" aria-hidden />
        <span className="font-semibold">DEV</span>
        {contact && <span className="max-w-40 truncate text-mute">{contact.accountName}</span>}
        {writesLive === false && <span className="text-rust">view-only contact</span>}
      </button>
    </div>
  )
}
